import { format, addDays, addWeeks, startOfWeek, endOfWeek } from 'date-fns';

/**
 * Cherche le prochain jour avec des cours dans une limite de jours
 * @param startDate - Date de départ
 * @param maxDays - Nombre maximum de jours à vérifier (défaut: 7)
 * @param fetchFunction - Fonction de fetch qui retourne les cours d'un jour
 * @returns La première date avec des cours, ou null si rien trouvé
 */
export async function findNextDayWithClasses(
  startDate: Date,
  maxDays: number = 7,
  fetchFunction: (dateISO: string) => Promise<any[]>
): Promise<{ date: Date; daysSkipped: number } | null> {
  
  for (let i = 1; i <= maxDays; i++) {
    const nextDate = addDays(startDate, i);
    const dateISO = format(nextDate, 'yyyy-MM-dd');
    
    try {
      const classes = await fetchFunction(dateISO);
      if (classes && classes.length > 0) {
        return { date: nextDate, daysSkipped: i };
      }
    } catch (error) {
      console.error(`Erreur lors de la recherche de cours pour ${dateISO}:`, error);
      // Continue la recherche même en cas d'erreur
    }
  }
  
  return null; // Aucun jour avec cours trouvé dans la limite
}

/**
 * Cherche la prochaine semaine avec des cours dans une limite de semaines
 * @param startDate - Date de départ
 * @param maxWeeks - Nombre maximum de semaines à vérifier (défaut: 4)
 * @param fetchFunction - Fonction de fetch qui retourne les cours d'une semaine
 * @returns La première semaine avec cours, ou null si rien trouvé
 */
export async function findNextWeekWithClasses(
  startDate: Date,
  maxWeeks: number = 4,
  fetchFunction: (startDate: Date, endDate: Date) => Promise<any[]>
): Promise<{ date: Date; weeksSkipped: number } | null> {
  
  for (let i = 1; i <= maxWeeks; i++) {
    const nextDate = addWeeks(startDate, i);
    const weekStart = startOfWeek(nextDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(nextDate, { weekStartsOn: 1 });
    
    try {
      const classes = await fetchFunction(weekStart, weekEnd);
      if (classes && classes.length > 0) {
        return { date: nextDate, weeksSkipped: i };
      }
    } catch (error) {
      console.error(`Erreur lors de la recherche de cours pour la semaine ${i}:`, error);
      // Continue la recherche même en cas d'erreur
    }
  }
  
  return null; // Aucune semaine avec cours trouvée dans la limite
}
